import { cloneDeep } from "lodash";
import {
  emptyWorksheetGridClientFilters,
} from "src/app/core/models/admissions.model";
import { SelectableEnumerationItem } from "src/app/core/models/lookup.model";
import { AdmissionsService } from "src/app/core/services/admissions.service";
import {
  AppliedFiltersState,
  DosFilter,
  FilterService,
} from "src/app/core/services/filter.service";
import { EngageReferral } from "src/app/core/services/engage-activity.service";
import { WORKSHEET_ALL_WORKFLOW_ID } from "./worksheet-queue-api.service";
import { worksheetAppliedFiltersActive } from "./worksheet-applied-filters.util";
import { WorksheetFilterSnapshot } from "./worksheet-saved-views.model";

/** Serializable {@link DosFilter} for `localStorage`. */
export interface PersistedDosFilter {
  dosStartIso: string | null;
  dosEndIso: string | null;
  resource: DosFilter["resource"];
  location: DosFilter["location"];
  encounterStatuses: DosFilter["encounterStatuses"];
}

export interface EngageWorklistPersistedFilterExtras {
  toolbarEngageSelections: EngageReferral[];
  physicianFilter: SelectableEnumerationItem[];
  appliedFilters: AppliedFiltersState;
  dosFilters: PersistedDosFilter;
  worklistEngagePatientId: string | null;
  /** Engage toolbar workflow picker (`all` or workflow UUID). */
  worksheetSelectedPipelineId?: string | null;
}

export function engageWorklistFiltersActive(
  admissions: AdmissionsService,
  filterService?: FilterService | null
): boolean {
  if (!admissions.isAllWorksheetWorkFlowSelected()) {
    return true;
  }
  if (worksheetAppliedFiltersActive(admissions)) {
    return true;
  }
  if ((admissions.worklistToolbarEngageSelections?.length ?? 0) > 0) {
    return true;
  }
  if (!filterService) {
    return false;
  }
  if ((filterService.getWorklistEngagePatientIdFilter() ?? "").trim()) {
    return true;
  }
  if (filterService.isEngageListClinicPreFilterApplied) {
    return true;
  }
  if (filterService.isDosFiltersApplied) {
    return true;
  }
  const c = filterService.getCurrentFilters();
  if ((c.referralIds?.length ?? 0) > 0) {
    return true;
  }
  if ((c.referralNotificationFilterKeys?.length ?? 0) > 0) {
    return true;
  }
  if (
    (c.stages?.length ?? 0) > 0 ||
    (c.assignedUsers?.length ?? 0) > 0 ||
    (c.channels?.length ?? 0) > 0 ||
    (c.notifyTypes?.length ?? 0) > 0 ||
    (c.statuses?.length ?? 0) > 0 ||
    (c.types?.length ?? 0) > 0 ||
    (c.tags?.length ?? 0) > 0 ||
    c.hasNotifications != null ||
    c.hasReminder != null ||
    (c.hasStarred ?? 0) !== 0 ||
    c.hasNewLead != null ||
    c.dateRange != null
  ) {
    return true;
  }
  const gt = admissions.worksheetGridText ?? emptyWorksheetGridClientFilters();
  if ((gt.referralNotificationFilters?.length ?? 0) > 0) {
    return true;
  }
  return false;
}

export function serializeDosFilter(dos: DosFilter): PersistedDosFilter {
  const range = dos.dos;
  const start = range?.[0];
  const end = range?.[1] ?? range?.[0];
  return {
    dosStartIso: start && !isNaN(start.getTime()) ? start.toISOString() : null,
    dosEndIso: end && !isNaN(end.getTime()) ? end.toISOString() : null,
    resource: dos.resource ?? null,
    location: dos.location ?? null,
    encounterStatuses: dos.encounterStatuses ? [...dos.encounterStatuses] : [],
  };
}

export function deserializeDosFilter(raw: PersistedDosFilter | null | undefined): DosFilter {
  if (!raw) {
    return {
      dos: null,
      resource: null,
      location: null,
      encounterStatuses: [],
    };
  }
  let dos: Date[] | null = null;
  if (raw.dosStartIso) {
    const s = new Date(raw.dosStartIso);
    const e = raw.dosEndIso ? new Date(raw.dosEndIso) : s;
    if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
      dos = [s, e];
    }
  }
  return {
    dos,
    resource: raw.resource ?? null,
    location: raw.location ?? null,
    encounterStatuses: raw.encounterStatuses ? [...raw.encounterStatuses] : [],
  };
}

export function serializeAppliedFiltersState(
  state: AppliedFiltersState
): AppliedFiltersState {
  const next = cloneDeep(state);
  if (next.dateRange?.startDate) {
    next.dateRange = {
      startDate: new Date(next.dateRange.startDate),
      endDate: new Date(next.dateRange.endDate),
    };
  }
  return next;
}

export function buildEngageWorklistPersistedExtras(
  admissions: AdmissionsService,
  filterService: FilterService
): EngageWorklistPersistedFilterExtras {
  return {
    toolbarEngageSelections: cloneDeep(admissions.worklistToolbarEngageSelections ?? []),
    physicianFilter: cloneDeep(admissions.physicianFilter ?? []),
    appliedFilters: serializeAppliedFiltersState(filterService.getCurrentFilters()),
    dosFilters: serializeDosFilter(filterService.getDosFilters()),
    worklistEngagePatientId: filterService.getWorklistEngagePatientIdFilter(),
    worksheetSelectedPipelineId:
      (admissions.worksheetSelectedPipelineId ?? "").trim() || WORKSHEET_ALL_WORKFLOW_ID,
  };
}

export function applyEngageWorklistPersistedExtras(
  admissions: AdmissionsService,
  filterService: FilterService,
  extras: EngageWorklistPersistedFilterExtras | null | undefined
): void {
  if (!extras) {
    return;
  }
  admissions.worklistToolbarEngageSelections = cloneDeep(extras.toolbarEngageSelections ?? []);
  if (Array.isArray(extras.physicianFilter)) {
    admissions.physicianFilter = cloneDeep(extras.physicianFilter);
  }
  filterService.setAllFilters(serializeAppliedFiltersState(extras.appliedFilters));
  filterService.setDosFilters(deserializeDosFilter(extras.dosFilters));
  filterService.setWorklistEngagePatientIdFilter(extras.worklistEngagePatientId ?? null);
  filterService.applyFilters();
  const pipelineId = (extras.worksheetSelectedPipelineId ?? "").trim();
  if (pipelineId) {
    admissions.worksheetSelectedPipelineId = pipelineId;
  }
}
